import React, { lazy, Suspense, useMemo, useState } from 'react';
import { Components } from 'react-markdown';
import { MessageModel, ModelByIDResponse } from '../../types/models';
import { Citation } from '../../types/models';
import { ReactComponent as ModelIcon } from '../../assets/edgerunnerLogo.svg';
import { FunctionResults } from '../FunctionResults';
import { ResponseMenu } from '../ResponseMenu';
import { useToastStore } from '../../stores/ToastStore';
import { useTTSStore } from '../../stores/ttsStore';
import { apiClient } from '../../clients/APIClient';
import { MarkdownRenderer } from './MarkdownRenderer';
import { CitationChips } from './CitationChips';
import {
	closeIncompleteLatex,
	trimIncompleteLatex,
} from '../../utils/latexProcessing';

const CitationDialog = lazy(() =>
	import('../CitationModal').then((m) => ({ default: m.CitationDialog })),
);
const ModelCardModal = lazy(() =>
	import('../ModelCardModal').then((m) => ({ default: m.ModelCardModal })),
);

interface AssistantMessageProps {
	message: MessageModel;
	messageIndex: number;
	model: string;
	citations?: Citation[];
	isStreaming: boolean;
	menuVisible: boolean;
	/** Pre-parsed display content (final answer). */
	assistantDisplayContent: string;
	/** Pre-parsed reasoning / analysis block. */
	assistantAnalysis?: string;
	stickyFinal: string;
	parsedFinal: string;
	/** react-markdown Components (with search highlighting wired in). */
	components: Components;
	/** react-markdown Components without search highlighting (for analysis/reasoning block). */
	analysisComponents: Components;
	messageContentRef: React.RefObject<HTMLDivElement | null>;
	handleCopy: (content: string) => void;
	handleCopyAsMarkdown: (content: string) => void;
	handleCopyEvent: (e: React.ClipboardEvent<HTMLDivElement>) => void;
	handleFeedback: (index: number, value: -1 | 1) => void;
}

export function AssistantMessage({
	message,
	messageIndex,
	citations,
	isStreaming,
	menuVisible,
	assistantDisplayContent,
	assistantAnalysis,
	stickyFinal,
	parsedFinal,
	components,
	analysisComponents,
	messageContentRef,
	handleCopy,
	handleCopyAsMarkdown,
	handleCopyEvent,
	handleFeedback,
}: AssistantMessageProps) {
	const { showToast } = useToastStore();
	const tts = useTTSStore();

	const [selectedCitation, setSelectedCitation] = useState<any | null>(null);
	const [selectedModelInfo, setSelectedModelInfo] = useState<ModelByIDResponse | null>(null);
	const [showAnalysis, setShowAnalysis] = useState(false);

	// TTS handlers
	const handleStartStreaming = async () => {
		const finalForTTS = (stickyFinal || parsedFinal || message.content)?.trim() ?? '';
		const ttsMessage = { ...message, content: finalForTTS };
		await tts.play(ttsMessage, true, 'en-us', 'response');
	};

	const handleModelNameClick = async () => {
		if (!message.model) return;
		setSelectedModelInfo({} as ModelByIDResponse);
		try {
			const modelInfo = await apiClient.models.getModelsInfoById(message.model);
			setSelectedModelInfo(modelInfo);
		} catch (error) {
			console.error('Error fetching model info:', error);
			showToast('Failed to load model information', 'error', { timeout: 3000, placement: 'chat' });
			setSelectedModelInfo(null);
		}
	};

	const rawContent = (assistantDisplayContent || '').trim();
	// Single trim/close scan, memoized on content. mathIsStreaming is derived
	// from the same result so streaming frames don't pay for two full scans.
	const processedContent = useMemo(
		() => (isStreaming ? trimIncompleteLatex(rawContent) : closeIncompleteLatex(rawContent)),
		[rawContent, isStreaming],
	);
	const mathIsStreaming =
		isStreaming && processedContent.length < rawContent.length;

	return (
		<>
			<div
				className="flex flex-row w-full overflow-hidden"
				style={{ maxWidth: '100%', minWidth: 0 }}
			>
				<ModelIcon className="p-1 ml-6 mr-2.5 w-10 h-10 flex-shrink-0 flex-grow-0 object-contain" />
				<div className="mr-6 flex flex-col" style={{ minWidth: 0 }}>
					<div
						ref={messageContentRef}
						className="bg-neutral-500 inline-block self-start px-lg py-sm rounded-[0px_10px_10px_10px] relative markdown-container"
						style={{
							wordWrap: 'break-word',
							overflowWrap: 'break-word',
							maxWidth: '100%',
							minWidth: 0,
							width: 'fit-content',
						}}
						onCopy={handleCopyEvent}
					>
						{/* Reasoning / analysis toggle */}
						{(assistantAnalysis || !assistantDisplayContent) && (
							<div className="mb-3">
								<details
									className="group"
									onToggle={(e) =>
										setShowAnalysis((e.target as HTMLDetailsElement).open)
									}
								>
									<summary className="list-none flex items-center gap-2 text-md text-neutral-100 hover:text-neutral-50 cursor-pointer select-none">
										<span
											className={`decoration-neutral-500/60 hover:decoration-neutral-100 ${
												!assistantDisplayContent && !showAnalysis
													? 'thinking-animate'
													: ''
											}`}
										>
											{showAnalysis
												? 'Hide reasoning'
												: assistantDisplayContent
													? 'Show reasoning'
													: 'Reasoning ...'}
										</span>
										<i
											className={`fas fa-chevron-right text-xs transition-transform duration-200 ${
												showAnalysis ? 'rotate-90' : ''
											}`}
											aria-hidden="true"
										/>
									</summary>

									{assistantAnalysis && (
										<div className="mt-4 mb-4 pl-4 border-l-4 border-neutral-300">
											<MarkdownRenderer
												content={assistantAnalysis}
												components={analysisComponents}
												className="text-md text-neutral-200 leading-6 [&>:last-child]:mb-0"
												isStreaming={isStreaming}
											/>
										</div>
									)}
								</details>
							</div>
						)}

						{/* Main response content */}
						<MarkdownRenderer
							content={processedContent}
							components={components}
							isStreaming={isStreaming}
							preProcessed
						/>
						{mathIsStreaming && (
							<div className="flex justify-center py-2">
								<span className="inline-flex items-end gap-[8px]">
									<span className="w-[4px] h-[4px] rounded-full bg-white animate-sine-1" />
									<span className="w-[4px] h-[4px] rounded-full bg-white animate-sine-2" />
									<span className="w-[4px] h-[4px] rounded-full bg-white animate-sine-3" />
								</span>
							</div>
						)}

						{/* Tool calls */}
						{message?.tool_calls?.map((toolCall, index) => (
							<FunctionResults
								key={index}
								functionName={toolCall?.function?.name}
								results={toolCall?.function?.results}
							/>
						))}

						{/* Citations */}
						<CitationChips citations={citations} onSelect={setSelectedCitation} />

						{selectedCitation && (
							<Suspense fallback={null}>
								<CitationDialog
									citation={selectedCitation}
									onClose={() => setSelectedCitation(null)}
								/>
							</Suspense>
						)}
					</div>

					{/* Model name + response menu */}
					<div className="flex justify-between items-center pt-xxs self-start w-full">
						<div
							className="text-neutral-300 text-sm font-primary ml-4 cursor-pointer hover:text-red-400 hover:underline transition-colors duration-200"
							onClick={handleModelNameClick}
							title="Click to view model card"
						>
							{message.model}
						</div>
						<ResponseMenu
							onCopy={() => handleCopy(message.content)}
							onCopyAsMarkdown={() =>
								handleCopyAsMarkdown(assistantDisplayContent || message.content)
							}
							onPlay={handleStartStreaming}
							onBadResponse={() => handleFeedback(messageIndex, -1)}
							onGoodResponse={() => handleFeedback(messageIndex, 1)}
							feedback={message.feedback}
							isVisible={menuVisible}
							onRegenerate={() =>
								console.warn('handleRegenerate is not implemented yet.')
							}
						/>
					</div>
				</div>
			</div>
			{selectedModelInfo && (
				<Suspense fallback={null}>
					<ModelCardModal
						modelInfo={selectedModelInfo}
						onClose={() => setSelectedModelInfo(null)}
					/>
				</Suspense>
			)}
		</>
	);
}
